import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useIntl } from "react-intl";
import AppState from "../store/AppState";
import {
  selectPinnedProductIds,
  selectIsProductListsFetched,
  selectGetProductIds,
  selectEditSequenceList,
  selectStoredPinnedProductIds,
  selectStoredUnpinnedProductIds,
  selectIsPreviewMode,
  selectSavingCategoryIds,
  selectUpdatedLocksInCategory,
} from "../store/product-list/ProductListSelectors";
import { selectCurrentCategory } from "../store/category/CategorySelectors";
import { selectCurrentCatalogId } from "../store/catalog/CatalogSelectors";
import {
  bulkSetPinnedUnPinnedProducts,
  setLockedUnlockedProducts,
} from "../store/product-list/ProductListActions";
import { Box, Typography, useTheme } from "@mui/material";
import { selectIsFetchedBySearch } from "../store/product/ProductSelectors";
import { isPinnedProduct } from "../utils/ProductUtil";
import {
  PINNED_TEXT_FIELD_INPUT_HEIGHT,
  styles,
} from "./PinnedProductsSelectStyles";
import TextFieldComponent from "./common/TextField";

const PinnedProductsSelect = () => {
  const intl = useIntl();
  const theme = useTheme();
  const { root, label } = styles(theme);

  const isFetchedBySearch = useSelector((state: AppState) =>
    selectIsFetchedBySearch(state),
  );

  const pinnedProductIdsFromDb = useSelector((state: AppState) =>
    selectPinnedProductIds(state),
  );
  const storedPinnedProductIds = useSelector((state: AppState) =>
    selectStoredPinnedProductIds(state),
  );
  const storedUnpinnedProductIds = useSelector((state: AppState) =>
    selectStoredUnpinnedProductIds(state),
  );

  const [value, setValue] = useState(0);
  const dispatch = useDispatch();

  const categoryId = useSelector((state: AppState) =>
    selectCurrentCategory(state),
  );
  const catalogId = useSelector((state: AppState) =>
    selectCurrentCatalogId(state),
  );

  const productIds = useSelector((state: AppState) =>
    selectGetProductIds(state),
  );
  const editSequenceList = useSelector((state: AppState) =>
    selectEditSequenceList(state),
  );
  const isProductListFetched = useSelector((state: AppState) =>
    selectIsProductListsFetched(state),
  );
  const isPreviewMode = useSelector((state: AppState) =>
    selectIsPreviewMode(state),
  );
  const savingCategoryIds = useSelector((state: AppState) =>
    selectSavingCategoryIds(state),
  );
  const isCurrentCategorySaving = savingCategoryIds.includes(categoryId);

  const locksForCategory = useSelector((state: AppState) =>
    selectUpdatedLocksInCategory(state, categoryId, catalogId ?? ""),
  );

  const lockedProductIdSet = useMemo(
    () => new Set(locksForCategory?.map((l) => l.productId) ?? []),
    [locksForCategory],
  );

  const productIdsInSequenceOrder = useMemo(() => {
    if (editSequenceList.length > 0) {
      return editSequenceList.map((p) => p.productId);
    }
    return productIds;
  }, [editSequenceList, productIds]);

  const maxProductsCount =
    productIdsInSequenceOrder.length > 0
      ? productIdsInSequenceOrder.length
      : productIds.length;

  const leadingPinnedCount = useMemo(() => {
    const stored = storedPinnedProductIds ?? [];
    const unstored = storedUnpinnedProductIds ?? [];
    const db = pinnedProductIdsFromDb ?? [];
    let count = 0;
    for (const id of productIdsInSequenceOrder) {
      if (lockedProductIdSet.has(id)) {
        continue;
      }
      if (isPinnedProduct(stored, unstored, db, id)) {
        count++;
      } else {
        break;
      }
    }
    return count;
  }, [
    productIdsInSequenceOrder,
    storedPinnedProductIds,
    storedUnpinnedProductIds,
    pinnedProductIdsFromDb,
    lockedProductIdSet,
  ]);

  const applyFirstNpins = useCallback(
    (n: number) => {
      if (
        !isProductListFetched ||
        isFetchedBySearch ||
        !catalogId ||
        !categoryId
      ) {
        return;
      }
      if (isPreviewMode || isCurrentCategorySaving) {
        return;
      }

      const capped = Math.max(0, Math.min(n, productIdsInSequenceOrder.length));
      const targetProductIds = productIdsInSequenceOrder.slice(0, capped);
      const targetProductIdSet = new Set(targetProductIds);

      const storedPinned = storedPinnedProductIds ?? [];
      const storedUnpinned = storedUnpinnedProductIds ?? [];
      const dbPinned = pinnedProductIdsFromDb ?? [];

      const effectivePinned = new Set<string>();
      for (const id of productIdsInSequenceOrder) {
        if (isPinnedProduct(storedPinned, storedUnpinned, dbPinned, id)) {
          effectivePinned.add(id);
        }
      }

      const toUnpin: string[] = [];
      effectivePinned.forEach((id) => {
        if (!targetProductIdSet.has(id)) {
          toUnpin.push(id);
        }
      });

      for (const id of targetProductIds) {
        if (lockedProductIdSet.has(id)) {
          const lock = locksForCategory?.find((l) => l.productId === id);
          if (lock) {
            dispatch(
              setLockedUnlockedProducts({
                productId: lock.productId,
                catalogId: lock.catalogId,
                categoryId: lock.categoryId,
                position: lock.position,
                isLocked: false,
              }),
            );
          }
        }
      }

      const toPin: string[] = [];
      for (const id of targetProductIds) {
        if (!effectivePinned.has(id)) {
          toPin.push(id);
        }
      }

      const indexOf = (id: string) => productIdsInSequenceOrder.indexOf(id);
      toUnpin.sort((a, b) => indexOf(b) - indexOf(a));
      toPin.sort((a, b) => indexOf(a) - indexOf(b));

      if (toUnpin.length > 0 || toPin.length > 0) {
        dispatch(
          bulkSetPinnedUnPinnedProducts({
            toUnpin,
            toPin,
            catalogId,
            categoryId,
          }),
        );
      }
    },
    [
      isProductListFetched,
      isFetchedBySearch,
      catalogId,
      categoryId,
      productIdsInSequenceOrder,
      storedPinnedProductIds,
      storedUnpinnedProductIds,
      pinnedProductIdsFromDb,
      lockedProductIdSet,
      locksForCategory,
      isPreviewMode,
      isCurrentCategorySaving,
      dispatch,
    ],
  );

  useEffect(() => {
    if (!isProductListFetched) {
      return;
    }
    if (maxProductsCount === 0) {
      setValue(0);
      return;
    }
    setValue(leadingPinnedCount);
  }, [categoryId, isProductListFetched, maxProductsCount, leadingPinnedCount]);

  const changeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;

    if (raw === "") {
      setValue(0);
      applyFirstNpins(0);
      return;
    }

    const n = Number(raw);
    if (isNaN(n) || n < 0) return;
    if (n > maxProductsCount) return;

    setValue(n);
    applyFirstNpins(n);
  };

  const keyDownHandler = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (value === 0 && /^\d$/.test(event.key)) {
      event.preventDefault();
      const n = Number(event.key);
      if (n > maxProductsCount) return;
      setValue(n);
      applyFirstNpins(n);
    }
  };

  return (
    <div>
      {!isFetchedBySearch && (
        <Box sx={root}>
          <Box component="div" sx={label}>
            <Typography variant="subHeader" component="span">
              {intl.formatMessage({
                id: "pinnedProductsSelect.pinText",
                defaultMessage: "Pin first",
              })}
            </Typography>
          </Box>
          <TextFieldComponent
            textFieldId="PinnedProductsSelect"
            variant="outlined"
            value={value.toString()}
            type="number"
            size="small"
            fullWidth={false}
            inputBaseHeight={PINNED_TEXT_FIELD_INPUT_HEIGHT}
            onChange={changeHandler}
            disabled={isPreviewMode || isCurrentCategorySaving}
            onKeyDown={keyDownHandler}
            inputProps={{
              min: 0,
              max: isProductListFetched ? maxProductsCount : 0,
              step: 1,
              onWheel: (event: React.WheelEvent<HTMLInputElement>) => {
                event.currentTarget.blur();
              },
            }}
          />
          <Box component="div" sx={label}>
            <Typography variant="subHeader" component="span">
              {intl.formatMessage({
                id: "pinnedProductsSelect.productsText",
                defaultMessage: "products",
              })}
            </Typography>
          </Box>
        </Box>
      )}
      {isFetchedBySearch && (
        <>
          <i className="fa fa-exclamation-circle" />
          {intl.formatMessage({
            id: "pinnedProductsSelect.notAvailableText",
            defaultMessage:
              "Pinned products not available for searching result.",
          })}
        </>
      )}
    </div>
  );
};

export default React.memo(PinnedProductsSelect);
